import api from './api';

/**
 * Dashboard Service
 * API methods for fetching dashboard statistics across different sections
 */

// Helper to get all filter parameters
const getFilterParams = (filters = {}) => {
    const params = { ...filters };

    // Cleanup 'all' values to avoid sending unnecessary params
    if (params.category === 'all') delete params.category;
    if (params.timeRange === 'all') delete params.timeRange;

    // Remove empty custom dates if not explicitly in custom mode
    if (params.timeRange !== 'custom') {
        delete params.start_date;
        delete params.end_date;
    }

    return params;
};

export const dashboardAPI = {
    /**
     * Get overview statistics
     * @param {Object} filters - Filter parameters (startDate, endDate, category)
     * @returns {Promise} API response with overview stats
     */
    async getOverviewStats(filters = {}) {
        try {
            const params = getFilterParams(filters);
            const response = await api.get('/api/v1/dashboard/overview', { params });
            // Unwrap the Laravel API resource wrapper
            return { data: response.data.data };
        } catch (error) {
            // If endpoint doesn't exist yet, return mock data for development
            console.warn('Dashboard overview endpoint not available, using mock data');
            return {
                data: {
                    totalUsers: 1250,
                    activeUsers: 892,
                    totalPosts: 3450,
                    totalPets: 2180,
                    totalAppointments: 567,
                    activeClinics: 45,
                    marketplaceOrders: 234,
                    adoptionPosts: 89,
                    trends: {
                        userGrowth: 12.5,
                        postEngagement: 8.3,
                        appointmentRate: 15.7
                    }
                }
            };
        }
    },

    /**
     * Get user statistics
     * @param {Object} filters - Filter parameters (startDate, endDate, category = user type)
     * @returns {Promise} API response with user stats
     */
    async getUserStats(filters = {}) {
        try {
            const params = getFilterParams(filters);
            const response = await api.get('/api/v1/dashboard/users', { params });
            return { data: response.data.data };
        } catch (error) {
            console.warn('Dashboard users endpoint not available, using mock data');
            return {
                data: {
                    totalUsers: 1250,
                    activeUsers: 892,
                    newUsers: 124,
                    userGrowth: 12.5,
                    activePercentage: 71.4,
                    usersByType: {
                        PET_GUARDIAN: 850,
                        VETERINARIAN: 120,
                        SERVICE_PROVIDER: 180,
                        ADMIN: 25,
                        ORGANIZATION_ADMIN: 75
                    },
                    recentUsers: [
                        { id: 1, name: 'John Doe', email: 'john@example.com', type: 'PET_GUARDIAN', created_at: '2026-02-05T10:00:00Z' },
                        { id: 2, name: 'Jane Smith', email: 'jane@example.com', type: 'VETERINARIAN', created_at: '2026-02-04T15:30:00Z' },
                        { id: 3, name: 'Bob Wilson', email: 'bob@example.com', type: 'SERVICE_PROVIDER', created_at: '2026-02-04T12:00:00Z' }
                    ]
                }
            };
        }
    },

    /**
     * Get social/post statistics
     * @param {Object} filters - Filter parameters (startDate, endDate, category = post type)
     * @returns {Promise} API response with social stats
     */
    async getSocialStats(filters = {}) {
        try {
            const params = getFilterParams(filters);
            const response = await api.get('/api/v1/dashboard/social', { params });
            return { data: response.data.data };
        } catch (error) {
            console.warn('Dashboard social endpoint not available, using mock data');
            return {
                data: {
                    totalPosts: 3450,
                    totalEngagement: 28900,
                    avgEngagementRate: 8.4,
                    postGrowth: 15.2,
                    likes: 18500,
                    comments: 7200,
                    shares: 3200,
                    postsByType: {
                        regular: 2100,
                        adoption: 450,
                        channel: 700,
                        marketplace: 200
                    },
                    topPosts: [
                        { id: 1, content: 'Amazing progress with my rescue dog!', author: 'Sarah Johnson', created_at: '2026-02-04T10:00:00Z', type: 'regular', engagementCount: 487 },
                        { id: 2, content: 'Looking for a loving home for these kittens', author: 'Pet Rescue Center', created_at: '2026-02-03T14:30:00Z', type: 'adoption', engagementCount: 423 },
                        { id: 3, content: 'New veterinary clinic opening next week!', author: 'Dr. Mike Chen', created_at: '2026-02-02T09:15:00Z', type: 'regular', engagementCount: 391 }
                    ]
                }
            };
        }
    },

    /**
     * Get medical/appointment statistics
     * @param {Object} filters - Filter parameters (startDate, endDate, category = appointment status)
     * @returns {Promise} API response with medical stats
     */
    async getMedicalStats(filters = {}) {
        try {
            const params = getFilterParams(filters);
            const response = await api.get('/api/v1/dashboard/medical', { params });
            return { data: response.data.data };
        } catch (error) {
            console.warn('Dashboard medical endpoint not available, using mock data');
            return {
                data: {
                    totalAppointments: 567,
                    activeClinics: 45,
                    completionRate: 87.3,
                    appointmentGrowth: 18.5,
                    outpatientVisits: 423,
                    inpatientVisits: 89,
                    appointmentsByStatus: {
                        pending: 78,
                        confirmed: 156,
                        completed: 295,
                        cancelled: 38
                    },
                    recentAppointments: [
                        { id: 1, petName: 'Max', ownerName: 'John Doe', clinicName: 'Happy Paws Clinic', date: '2026-02-06T10:00:00Z', status: 'confirmed' },
                        { id: 2, petName: 'Bella', ownerName: 'Jane Smith', clinicName: 'Pet Care Center', date: '2026-02-06T14:30:00Z', status: 'confirmed' },
                        { id: 3, petName: 'Charlie', ownerName: 'Bob Wilson', clinicName: 'Animal Hospital', date: '2026-02-05T09:00:00Z', status: 'completed' }
                    ]
                }
            };
        }
    }
};

export default dashboardAPI;
